import React, { FunctionComponent, ReactNode } from 'react'

import { isDate } from 'date-fns'
import { SpaceProps, WidthProps } from 'styled-system'

import { CopyToClipboard } from '@/atoms/copy-to-clipboard'
import { InfoTooltip } from '@/atoms/info-tooltip'
import { Box, Flex } from '@/atoms/layout'
import { Label } from '@/atoms/typography'
import { LabelWrapper } from '@/components/info-field/atoms/styles'
import { ShowMoreText } from '@/components/show-more-text'
import { formatDate } from '@/helpers/date'
import { formatCurrency, formatDays, formatMonths, formatNumber, formatPercent } from '@/helpers/number'
import { isEmpty, isNil } from '@/helpers/type-guards'
import { Currency, ValueFormatType } from '@/models/common'
import { Color, Space } from '@/styles'

import { Text } from './atoms'

export interface InfoFieldProps {
  label?: ReactNode
  name?: string
  fieldValue?: string | number | object | boolean | Date
  currency?: Currency
  copyValue?: string
  valueFormat?: ValueFormatType
  fieldProps?: (SpaceProps & WidthProps) | WidthProps
  color?: Color
  decimalScale?: number
  bold?: boolean
  tooltip?: string
}

const formatValue = ({
  value,
  valueFormat,
  currency,
  decimalScale,
}: {
  valueFormat: ValueFormatType
  value: string | number | object | boolean | Date
  decimalScale: number
  currency: Currency
}): string | number => {
  switch (valueFormat) {
    case ValueFormatType.CURRENCY:
      return formatCurrency(value as number, { fractionDigits: decimalScale, currency })
    case ValueFormatType.PERCENTAGE:
      return formatPercent(value as number, { fractionDigits: decimalScale })
    case ValueFormatType.DAYS:
      return formatDays(value as number)
    case ValueFormatType.MONTHS:
      return formatMonths(value as number)
    case ValueFormatType.DATE:
      return formatDate(value as string)
    case ValueFormatType.NUMBER:
      return formatNumber(value as number, { fractionDigits: decimalScale })

    default: {
      if (isNil(value) || isEmpty(value) || !value) return '-'
      return typeof value === 'number' ? value : String(value)
    }
  }
}
const getValue = (
  valueFormat: ValueFormatType,
  value: number | string,
  color: Color,
  decimalScale: number,
  bold: boolean,
  currency: Currency,
): ReactNode => {
  if (valueFormat === ValueFormatType.TEXTAREA) return <ShowMoreText text={value as string} />

  const formatted = formatValue({ valueFormat, value, decimalScale, currency })

  return (
    <Text color={color} title={formatted == null ? '' : String(formatted)} fontWeight={bold ? 700 : 400}>
      {formatted}
    </Text>
  )
}

const InfoField: FunctionComponent<InfoFieldProps> = ({
  label,
  copyValue,
  fieldValue,
  currency = Currency.EUR,
  decimalScale,
  valueFormat,
  color,
  bold = false,
  tooltip,
  fieldProps,
}: InfoFieldProps) => {
  const safeValue =
    typeof fieldValue === 'object' && fieldValue !== null && !React.isValidElement(fieldValue) && !isDate(fieldValue)
      ? JSON.stringify(fieldValue)
      : fieldValue

  return (
    <Flex {...fieldProps} alignItems="flex-start" justifyContent="flex-start" flexDirection="column">
      {(label || copyValue) && (
        <LabelWrapper>
          {label && <Label>{label}</Label>}
          {copyValue && (
            <Box ml={Space.S8}>
              <CopyToClipboard value={copyValue} />
            </Box>
          )}
          {tooltip && <InfoTooltip tooltipText={tooltip} pl={Space.S8} pb={Space.S2} />}
        </LabelWrapper>
      )}
      <Flex mt={Space.S2} width={1}>
        {React.isValidElement(safeValue)
          ? safeValue
          : getValue(valueFormat, safeValue as number | string, color, decimalScale, bold, currency)}
      </Flex>
    </Flex>
  )
}

InfoField.displayName = 'InfoField'

export default InfoField
